<template>
  <v-card flat v-if="showLayout">
    <v-progress-linear v-if="showProgress && !gettingMore" active :indeterminate="true" color="accent" height="2"></v-progress-linear>
    <v-data-table
      :items=items
      hide-default-footer
      hide-default-header
      :no-data-text="loading ? 'fetching' : 'none found'"
      :items-per-page="(items && items.length) || 10"
      disable-sort
    >
      <template v-slot:item="{item}">
        <Itemrow 
          :item="item"
          :playing="playing" 
          compressed
          :row-type="targetType"
          :all-prizes="AllPrizes">
        </Itemrow>
      </template>
    </v-data-table>

    <video-count-chip :loading="loading" :totalItems="totalItems" :totalFetched="totalFetched"></video-count-chip>

    <infinite-loading v-if="needScrolling" :distance="scrollDistance" @infinite="scrollMore">
      <template v-slot:spinner>
        <v-progress-linear :active="showProgress" :indeterminate="true" color="accent" height="2"></v-progress-linear>
      </template>
      <template v-slot:no-more><span></span></template>
      <template v-slot:no-results><span v-if="!totalFetched">"no matches found"</span><span v-else></span></template>
    </infinite-loading>
    

  </v-card>

  <Nothing v-else-if="!searchText"></Nothing>

</template>
<script>

import { SearchUtils, is } from '@/js'
import Videocountchip from '@/components/Videocountchip'
import { 
  ElDefaults, 
  Controls, 
  SearchVariables, 
  SearcherResult, 
  AllPrizes, 
  SearchVariableFilters 
} from '@/gql'
import Itemrow from '@/components/Itemrow'
import Nothing from '@/components/Nothing'
import { removeTypename } from '@/js/apollo'
import { settings, getIconColor } from '@/js/store'
import InfiniteLoading from 'vue-infinite-loading'
import { mapState } from 'vuex'
import { vxMaps } from '@/js/vxstore'

// this is shared between all item types
export default {
  components: {
    Itemrow,
    Nothing,
    'infinite-loading':InfiniteLoading,
    'video-count-chip': Videocountchip
  },
  computed: {
    elDefaults () {
      if(!this.ElDefaults) return null
      const da = this.ElDefaults && 
        this.ElDefaults.Defaults.filter(f=>f.itemType === this.targetType)
      if(!da || !da.length){
        console.error('missing eldefaults for', this.targetType)
        return null
      }
      const df = da[0].DefaultIncludes
      if(!df) {
        console.error('no default includes for', this.targetType, da)
      }
      // dump the __typename
      return df.map(d=>({
        key: d.key,
        weight: d.weight
      }))
    },
    defaultConfidence () {
      return this.viSlider/ 100
    },
    useElastic () {
      return this.ElDefaults && this.ElDefaults.ElUses.find(g=>g===this.targetType)
    },
    searcherType () {
      return this.useElastic ? 'ELASTIC' : 'DB'
    },
    needScrolling () {
      return this.totalItems && this.totalFetched >= settings.pagination.initialLimit && this.totalFetched < this.totalItems
    },
    showFetchedCount () {
      return !is.nullOrUndefined(this.totalItems)
    },
    totalItems () {
      const totalItems = (this.SearcherResult && this.SearcherResult.totalRows)
      return totalItems
    },
    progressColor () {
      return getIconColor(this.totalItems === this.totalFetched ? (this.totalItems ? 'done' : 'none') : 'progress')
    },
    targetType () {
      return this.Controls && this.Controls.targetType
    },
    queryVars () {
      // query construction
      if (!this.SearchVariables) return null

      const q = {
        ...this.SearchVariables,
        filters: removeTypename((this.searchVariableFilter && this.searchVariableFilter.value) || []),
        limit: settings.pagination.initialLimit,
        value: this.useElastic ? this.searchText : '%' + this.searchText + '%',
        offset: 0,
        itemType: this.targetType,
        searcherType: this.searcherType,
        // because at the stage we only want to see the first master TODO
        masterLimit: 1
      }
      if (this.useElastic) {
        q.defaultConfidence = this.defaultConfidence
        q.elIncludes = this.elDefaults
      }
      return q
    },
    searchVariableFilter () {
      // these are the reasons that are currently enabled by the user for this type of search
      return this.SearchVariableFilters
        ? this.SearchVariableFilters.SearchFilters.find(f => f.itemType === this.targetType)
        : null
    },
    loading () {
      return this.$apollo.loading
    },
    showProgress () {
      return this.loading
    },
    searchText () {
      return this.Controls && this.Controls.searchText
    },
    showLayout () {
      return this.totalFetched > 0 || this.searchText
    },
    showTable () {
      return this.items && this.items.length
    },
    playing () {
      return this.Controls && this.Controls.playing
    },
    noDataText () {
      return this.loading ? ' ' : settings.pagination.noDataText
    },
    ...mapState(vxMaps.state)
  },
  mounted () {
    // if we need to resize, then some of the variable assumptions will change
    this.onResize()
  },
  methods: {
    onResize () {
      // this is used to readjust rows per page and various other responsive things
      this.windowHeight = window.innerHeight
      this.windowWidth = window.innerWidth
    },
    registerResult (data) {
      this.SearcherResult = data && data.SearcherResult
      const fetchedRows = ( this.SearcherResult && this.SearcherResult.Searchers) || []
      this.totalFetched = fetchedRows.length
      if (this.totalFetched <= settings.pagination.initialLimit) {
        this.infiniteID++
      }
      SearchUtils.optimizeFetched({ fetched: fetchedRows })
      fetchedRows.forEach(d => {
        // we're looking for something like filmID
        const idField = d.itemType + 'ID'
        d.itemReason = d.MatchReasons.find(f => f[idField] === d.itemID)
        if (!d.itemReason) {
          console.log(`itemtable: failed to find itemreason for ${idField}`, d)
        }
        if (d.itemType !== this.targetType) {
           console.log(`itemtable:expected ${this.targetType} but got ${fetchedRows.map(f => f.itemType).join(',')}`)
        }
      })
      // cause a refresh
      this.items = fetchedRows ? JSON.parse(JSON.stringify(fetchedRows)) : fetchedRows

    },
    scrollMore (scrollState) {
      const offset = this.totalFetched
      if (offset >= this.totalItems) {
        scrollState.loaded()
        scrollState.complete()
        return null
      }

      // start getting more
      const { $apollo, queryVars } = this
      const { queries } = $apollo
      this.gettingMore = true

      // do the query
      return queries.SearcherResult.fetchMore({
        variables: {
          ...queryVars,
          limit: settings.pagination.limit,
          offset
        },
        query: SearcherResult,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          // the newly returned data
          const newData = fetchMoreResult.SearcherResult.Searchers

          // what we already have
          const chunk = previousResult.SearcherResult.Searchers

          // its not a pure append, because its possoble we've paged beyond what we already have
          Array.prototype.splice.apply(chunk, [offset, 0].concat(newData))

          // reconstruct a full result, but with the additional data and resgister
          const result = {
            totalRows: fetchMoreResult.SearcherResult.totalRows,
            Searchers: chunk
          }

          // using object assign to inherit the object details from the original request
          const ob = Object.assign({}, previousResult, result)

          // no need to register result process, as the result callback in the main query will be called and take care of it
          this.gettingMore = false
          scrollState.loaded()
          return ob
        }
      })
    }
  },
  name: 'item-table',
  data: () => ({
    // used for scrolling
    infiniteID: 0,
    windowHeight: 0,
    windowWidth: 0,
    scrollDistance: settings.pagination.scrollDistance,
    SearchVariableFilters: null,
    gettingMore: false,
    // used for elastic searching configuration
    ElDefaults: null,
    items: [],
    // this is a list of all know prizes by award
    // generally it'll be in cache
    AllPrizes: [],
    // state management for controls from other pages
    Controls: {},
    // apollo puts the search result here
    SearcherResult: null,
    // state management to control the variables for a gql search
    SearchVariables: {},
    totalFetched: 0
  }),
  apollo: {
    SearchVariableFilters: {
      query: SearchVariableFilters
    },
    SearchVariables: {
      query: SearchVariables
    },
    ElDefaults: {
      query: ElDefaults
    },
    Controls: {
      query: Controls
    },
    AllPrizes: {
      query: AllPrizes
    },
    SearcherResult: {
      query: SearcherResult,
      variables () {
        return this.queryVars
      },
      skip () {
        // don't want to do this if not ready
        return !this.Controls || !this.searchText || !this.targetType || !this.queryVars
      },
      result (result) {
        // this actually handles registration of data for both new and get more stuff
       
        this.registerResult(result && result.data)
      }
    }
  }
}
</script>
